import { useTaskWorkerActions } from "@/hooks/use-task-worker-actions";
import { contextUsagePercent } from "@/lib/context-usage";
import { formatModelLabel } from "@/lib/model-registry";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import type { InputBarProps, InputBarWorkerControls } from "./input-bar-types";
import type { WorkerComposerAlertsProps } from "./worker-composer-alerts";

/** A task-chat composer is literal text only; Stop never reaches Denker. */
export const TASK_CHAT_INPUT_BAR_PROPS = {
  textOnly: true,
  images: [],
  onImageFile: undefined,
  onRemoveImage: undefined,
  slashCommands: [],
  onSlashCommand: undefined,
  // Hotkey speech is for Denker; a worker bar only takes its own mic's speech.
  liveDictation: false,
  isStreaming: false,
  onStop: undefined,
  agents: [],
  selectedAgentIds: undefined,
  focusedAgent: null,
  providers: [],
  onUpdateAgentModel: undefined,
  onUpdateAgentEffort: undefined,
} satisfies Partial<InputBarProps>;

const CODING_APP_LABELS: Record<string, string> = {
  claude: "Claude",
  codex: "Codex",
  opencode: "OpenCode",
};

/** Worker state, controls and send gating shared by every task-chat composer. */
export function useWorkerComposer({
  enabled,
  projectId,
  taskId,
  onSubmit,
  disabled,
  placeholder,
}: {
  enabled: boolean;
  projectId: string | undefined;
  taskId: string | undefined;
  onSubmit: InputBarProps["onSubmit"];
  disabled?: boolean;
  placeholder?: string;
}) {
  // Without a task id the worker is never fetched or polled.
  const t = useT(inputBarMessages);
  const { worker, error, busy, actionError, refresh, run, control } =
    useTaskWorkerActions(projectId, enabled ? taskId : undefined);
  const pendingQuestion = enabled ? (worker?.pending_question ?? null) : null;
  // The backend answers a sole live question with the next composer message,
  // even while instructions wait on a pending control.
  const answersQuestion = pendingQuestion?.composer_can_answer === true;
  const blocked =
    enabled && ((!worker?.can_instruct && !answersQuestion) || !!error);
  const reason =
    error ||
    worker?.control_unavailable_reason ||
    worker?.resume_unavailable_reason ||
    (worker ? t("chatCannotReceive") : t("loadingTaskControls"));
  // The user's app holds the chat: a soft hold, so input stays enabled and
  // every send re-probes; the next snapshot without the code clears it.
  const appLabel =
    CODING_APP_LABELS[worker?.provider ?? ""] ?? t("yourCodingApp");
  const appHoldPlaceholder =
    enabled && worker?.control_unavailable_code === "app_holds_session"
      ? t("openInAppToContinue", { app: appLabel })
      : null;
  // A headless turn ended: the user may go on here or in their app.
  const afterTurnPlaceholder =
    enabled && worker?.status === "idle" && worker.app_view === "after_turn"
      ? t("doneContinueHereOrInApp", { app: appLabel })
      : null;
  const contextUsage =
    enabled &&
    worker?.context_used_tokens != null &&
    worker.context_window_max != null &&
    contextUsagePercent({
      used: worker.context_used_tokens,
      max: worker.context_window_max,
    }) !== null
      ? { used: worker.context_used_tokens, max: worker.context_window_max }
      : null;
  const workerControls: InputBarWorkerControls | undefined = worker
    ? {
        modelLabel:
          worker.model_source === "observed" && worker.model
            ? formatModelLabel(`${worker.provider}_cli`, worker.model)
            : null,
        contextUsage,
        queuedCount: worker.queued_instruction_count,
        control,
      }
    : undefined;
  const alerts: WorkerComposerAlertsProps = {
    alert:
      error ||
      actionError ||
      worker?.control_error ||
      worker?.failure_reason ||
      null,
    // The question card already says the worker is waiting.
    waitingForReply: !!worker?.pending_input_count && !pendingQuestion,
    canOpenParent:
      !!worker?.parent_conversation_id &&
      (worker.connection_status === "offline" || !!worker.control_error),
    busy,
    onOpenParent: () => void run("open-parent"),
  };
  const submitToWorker = async () => {
    if (blocked) return false;
    const sent = await onSubmit();
    refresh();
    return sent;
  };
  return {
    alerts,
    workerControls,
    /** Provider of the live worker, if known — enables attachment delivery. */
    workerProvider: worker?.provider,
    question: pendingQuestion && {
      awaitingInputId: pendingQuestion.awaiting_input_id,
      question: pendingQuestion.question,
      options: pendingQuestion.options,
    },
    onQuestionAnswered: refresh,
    /** The worker can't take a message (its controls are loading, it can't be
     *  instructed, or it failed); unlike `disabled`, a host's own disable is
     *  not part of it. */
    blocked,
    disabled: !!disabled || blocked,
    placeholder: blocked
      ? reason
      : (placeholder ??
        appHoldPlaceholder ??
        afterTurnPlaceholder ??
        (answersQuestion
          ? t("answerQuestion")
          : enabled
            ? t("addInstructions")
            : undefined)),
    onSubmit: enabled ? submitToWorker : onSubmit,
  };
}
